/**
 * Tunnel Display Mapper - UI Vocabulary Transformation
 * 
 * TUNNEL_UI_DEV_PLAN_v2.md - Bucket 1 Deliverable (created in Bucket 0 for structure)
 * 
 * CRITICAL RULE: This is the ONLY place where backend→UI naming happens.
 * Backend payloads are NEVER modified before sending.
 * 
 * Vocabulary Mapping:
 * | UI Display     | Backend Field        |
 * |----------------|---------------------|
 * | Cap            | units               |
 * | Cap Space      | initial_bankroll - used_units |
 * | Lock In Play   | POST /entry/save    |
 * | Stat Target    | prop.stat_type      |
 */

import type { RosterSlot } from '../utils/rosterHelpers';

// =============================================================================
// DISPLAY TRANSFORMATIONS (Backend → UI)
// =============================================================================

/**
 * Formats units for display as "Cap".
 * 
 * @param units - Backend units value
 * @returns Formatted display string
 */
export const toDisplayCap = (units: number): string => {
  return `${units} Cap`;
};

/**
 * Formats remaining bankroll as "Cap Space".
 * 
 * @param remaining - Remaining units available
 * @returns Formatted display string
 */
export const toDisplayCapSpace = (remaining: number): string => {
  return `${remaining} Cap Space`;
};

/**
 * Formats stat type for UI display.
 * Maps backend stat_type codes to user-friendly labels.
 * 
 * @param statType - Backend stat type (e.g., 'PASSING_YARDS')
 * @returns User-friendly label (e.g., 'Pass Yds')
 */
export const toDisplayStatTarget = (statType: string): string => {
  const statMap: Record<string, string> = {
    'PASSING_YARDS': 'Pass Yds',
    'RUSHING_YARDS': 'Rush Yds',
    'RECEIVING_YARDS': 'Rec Yds',
    'PASSING_TOUCHDOWNS': 'Pass TDs',
    'RUSHING_TOUCHDOWNS': 'Rush TDs',
    'RECEIVING_TOUCHDOWNS': 'Rec TDs',
    'RECEPTIONS': 'Receptions',
    'COMPLETIONS': 'Completions',
    'INTERCEPTIONS': 'INTs',
    'PASSING_ATTEMPTS': 'Pass Att',
    'RUSHING_ATTEMPTS': 'Rush Att',
    'TARGETS': 'Targets',
  };
  
  return statMap[statType] || statType;
};

/**
 * Gets confidence level label based on units allocated.
 * 
 * @param units - Units allocated to this play
 * @param maxUnits - Maximum possible units (default: 50)
 * @returns Confidence label
 */
export const toConfidenceLabel = (units: number, maxUnits: number = 50): string => {
  const percentage = (units / maxUnits) * 100;
  
  if (percentage <= 30) return 'Conservative';
  if (percentage <= 60) return 'Standard';
  return 'Blitz';
};

// =============================================================================
// API PAYLOAD CONSTRUCTION (UI → Backend)
// =============================================================================

/**
 * UI slot representation (uses "cap" internally)
 */
export interface UISlot {
  slotIndex: number;
  player: { id: number } | null;
  prop: { id: number } | null;
  side: 'OVER' | 'UNDER' | null;
  cap: number;  // UI calls it "cap"
}

/**
 * Backend API slot format
 */
export interface ApiSlot {
  slot_index: number;
  player_prop_id: number | null;
  side: 'OVER' | 'UNDER' | null;
  units: number;  // API expects "units"
}

/**
 * Converts a UI slot to API payload format.
 * 
 * CRITICAL: This is where "cap" becomes "units" for the API.
 * The transformation happens HERE and ONLY here.
 * 
 * @param slot - UI slot with "cap" field
 * @returns API-ready slot with "units" field
 */
export const toApiSlot = (slot: UISlot): ApiSlot => ({
  slot_index: slot.slotIndex,
  player_prop_id: slot.prop?.id ?? null,
  side: slot.side,
  units: slot.cap,  // UI "cap" → API "units"
});

/**
 * Converts a RosterSlot to API payload format.
 * 
 * @param slot - Standard roster slot
 * @param index - Slot index
 * @returns API-ready slot
 */
export const rosterSlotToApiSlot = (slot: RosterSlot, index: number): ApiSlot => ({
  slot_index: index,
  player_prop_id: slot.prop?.id ?? null,
  side: slot.side,
  units: slot.units,
});

/**
 * Builds complete roster payload for API submission.
 * 
 * @param roster - Array of roster slots
 * @returns API request body
 */
export const buildRosterPayload = (roster: RosterSlot[]): { roster: ApiSlot[] } => ({
  roster: roster.map((slot, index) => rosterSlotToApiSlot(slot, index)),
});

// =============================================================================
// MATCHUP GRADE DISPLAY
// =============================================================================

export interface MatchupGrade {
  grade: string;
  color: string;
  label: string;
}

/**
 * Calculates matchup grade based on opponent defense ranking.
 * Higher rank = weaker defense = better matchup.
 * 
 * @param defRank - Opponent defense rank (1-32, higher is weaker)
 * @returns Grade object with display properties
 */
export const getMatchupGrade = (defRank: number): MatchupGrade => {
  if (defRank >= 25) return { grade: 'A+', color: '#10B981', label: 'Elite' };
  if (defRank >= 20) return { grade: 'A', color: '#34D399', label: 'Favorable' };
  if (defRank >= 15) return { grade: 'B+', color: '#FBBF24', label: 'Average' };
  if (defRank >= 10) return { grade: 'B', color: '#F59E0B', label: 'Neutral' };
  if (defRank >= 5) return { grade: 'C', color: '#F97316', label: 'Tough' };
  return { grade: 'D', color: '#EF4444', label: 'Avoid' };
};

/**
 * Gets color for opponent indicator based on defense rank.
 * 
 * @param defRank - Opponent defense rank
 * @returns Hex color string
 */
export const getOpponentColor = (defRank: number): string => {
  if (defRank >= 20) return '#10B981';  // Green - favorable
  if (defRank >= 10) return '#FBBF24';  // Yellow - neutral
  return '#EF4444';  // Red - tough
};

// =============================================================================
// CAP SPACE RING COLORS
// =============================================================================

/**
 * Gets color for cap space ring based on remaining percentage.
 * 
 * @param remaining - Remaining units
 * @param total - Total capacity
 * @returns Hex color string
 */
export const getCapRingColor = (remaining: number, total: number): string => {
  const percentage = (remaining / total) * 100;
  
  if (percentage > 50) return '#10B981';  // Green - plenty remaining
  if (percentage > 20) return '#F59E0B';  // Amber - moderate
  return '#EF4444';  // Red - low
};
